import { useState } from "react";
import { Link } from "react-router-dom";
import { copyCode } from "@/lib/clipboard";
import { Copy, ExternalLink, ChevronDown, Clock, ShieldCheck, Ticket } from "lucide-react";
import { couponGoUrl, fmtDate } from "@/lib/api";
import { DemoBadge } from "@/components/site/DealCard";

const daysLeft = (iso) => {
  if (!iso) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((new Date(iso.slice(0, 10) + "T00:00:00") - today) / 86400000);
};

const Expiry = ({ iso }) => {
  const d = daysLeft(iso);
  if (d === null) return <span className="text-slate-500">No end date given by the store</span>;
  return (
    <span className={d <= 3 ? "text-red-600 font-semibold" : "text-slate-600"}>
      Valid till {fmtDate(iso)}{d <= 7 && ` (${d <= 0 ? "ends today" : `${d} day${d === 1 ? "" : "s"} left`})`}
    </span>
  );
};

export const CouponRow = ({ c, showStore = false }) => {
  const [open, setOpen] = useState(false);
  const use = async () => {
    if (c.code) {
      await copyCode(c.code, `Code ${c.code} copied - paste it at checkout`);
    }
    window.open(couponGoUrl(c.id), "_blank", "noopener");
  };
  return (
    <li id={`coupon-${c.id}`} className="bg-white border border-slate-200 rounded-xl overflow-hidden scroll-mt-36" data-testid={`store-coupon-${c.id}`}>
      <div className="flex flex-col sm:flex-row">
        <div className={`sm:w-28 shrink-0 flex sm:flex-col items-center justify-center gap-1 px-4 py-3 text-xs font-extrabold uppercase tracking-wide ${c.code ? "bg-amber-100 text-amber-900" : "bg-emerald-50 text-emerald-800"}`}>
          <Ticket size={18} /> {c.code ? "Code" : "Offer"}
        </div>
        <div className="flex-1 p-4 space-y-1.5 min-w-0">
          {showStore && <Link to={`/store/${c.merchant_slug || ""}`} className="text-xs font-extrabold uppercase tracking-wide text-emerald-700" data-testid="coupon-row-store">{c.merchant_name}</Link>}
          <div className="flex items-start gap-2"><p className="font-bold text-slate-900">{c.title}</p><DemoBadge show={c.is_demo} /></div>
          <p className="text-xs flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="flex items-center gap-1"><Clock size={12} /><Expiry iso={c.expires_at} /></span>
            {c.verified_on && <span className="flex items-center gap-1 text-slate-500"><ShieldCheck size={12} /> Last updated from the network {fmtDate(c.verified_on)}</span>}
          </p>
          {(c.description || c.terms) && (
            <button onClick={() => setOpen(!open)} className="text-xs font-semibold text-emerald-700 flex items-center gap-1" data-testid="store-coupon-details-toggle">
              Details & terms <ChevronDown size={12} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>
          )}
          {open && <div className="text-xs text-slate-600 space-y-1 pt-1" data-testid="store-coupon-details">{c.description && <p>{c.description}</p>}{c.terms && <p className="text-slate-500">Terms: {c.terms}</p>}</div>}
        </div>
        <div className="p-4 flex sm:flex-col items-center justify-center gap-2 border-t sm:border-t-0 sm:border-l border-dashed border-slate-200 sm:w-48">
          {c.code && <code className="font-mono font-bold tracking-wider text-amber-900 bg-amber-50 border border-dashed border-amber-400 px-3 py-1 rounded" data-testid="store-coupon-code">{c.code}</code>}
          <button onClick={use} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-1 transition-colors w-full justify-center" data-testid="store-coupon-use-button">
            {c.code ? <><Copy size={14} /> Copy & shop</> : <>Shop offer <ExternalLink size={13} /></>}
          </button>
          {c.lands_on_homepage && <p className="text-[11px] text-slate-500 text-center leading-tight" data-testid="store-coupon-homepage-note">Opens {c.merchant_name} home page - store-wide code, apply it at checkout</p>}
        </div>
      </div>
    </li>
  );
};

const STEPS = ["Tap “Copy & shop”. The code is copied and the store opens in a new tab.", "Add the products you want to your cart.",
  "At checkout, paste the code in the “Coupon / Promo code” box and apply it.", "Check the discount appears before you pay. If it doesn't, the offer may have changed - try another code."];

const HowTo = ({ name }) => (
  <aside className="bg-slate-900 text-white rounded-xl p-5 space-y-4 h-fit lg:sticky lg:top-24" data-testid="store-how-to">
    <h2 className="font-bold text-lg">How to use a {name} coupon</h2>
    <ol className="space-y-3 text-sm text-slate-200">
      {STEPS.map((t, i) => (
        <li key={t} className="flex gap-3"><span className="h-6 w-6 shrink-0 rounded-full bg-amber-400 text-slate-900 font-extrabold text-xs flex items-center justify-center">{i + 1}</span>{t}</li>
      ))}
    </ol>
    <p className="text-xs text-slate-400 border-t border-white/10 pt-3">Offers come from the store's affiliate network. We can't guarantee every code works for every order. We may earn a commission at no extra cost to you.</p>
  </aside>
);

export const StoreCoupons = ({ store }) => {
  const list = [...(store.coupons || [])].sort((a, b) => (!!b.code - !!a.code) || ((a.expires_at || "9999") < (b.expires_at || "9999") ? -1 : 1));
  if (!list.length) return null;
  const codes = list.filter((c) => c.code).length;
  return (
    <section className="mt-8" data-testid="store-coupons">
      <p className="text-sm text-slate-600 mb-4" data-testid="store-coupons-count"><b>{codes}</b> coupon code{codes === 1 ? "" : "s"} · <b>{list.length - codes}</b> offer{list.length - codes === 1 ? "" : "s"} without a code</p>
      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <details className="lg:hidden bg-slate-900 text-white rounded-xl px-5 py-3" data-testid="store-how-to-mobile">
          <summary className="font-bold cursor-pointer">How to use a {store.name} coupon</summary>
          <ol className="list-decimal ml-5 mt-2 space-y-1 text-sm text-slate-200">
            {STEPS.map((t) => <li key={t}>{t}</li>)}
          </ol>
        </details>
        <ul className="space-y-3">{list.map((c) => <CouponRow key={c.id} c={c} />)}</ul>
        <div className="hidden lg:block"><HowTo name={store.name} /></div>
      </div>
    </section>
  );
};
